import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldExclamation } from 'react-bootstrap-icons';
import { styled, keyframes } from '../../styles/stitches.config';
import { Button } from '../../styles/_app.style';

/* ------------------------------------------------------------------ */
/*  Animations                                                         */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const slideUp = keyframes({
  from: { opacity: 0, transform: 'translateY(12px) scale(0.97)' },
  to: { opacity: 1, transform: 'translateY(0) scale(1)' },
});

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Backdrop = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  animation: `${fadeIn} 300ms ease-out`,
});

const Card = styled('div', {
  maxWidth: '420px',
  width: '90%',
  borderRadius: '16px',
  backgroundColor: '#fff',
  padding: '2.5rem 2rem 2rem',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  animation: `${slideUp} 400ms ease-out`,
});

const IconCircle = styled('div', {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  margin: '0 auto 1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    variant: {
      pending: {
        backgroundColor: '#FFF3CD',
        color: '#856404',
      },
      incomplete: {
        backgroundColor: '#F8D7DA',
        color: '#721C24',
      },
    },
  },
});

const Title = styled('h2', {
  fontSize: '1.25rem',
  fontWeight: 700,
  margin: '0 0 0.5rem',
  color: '#1a1a1a',
});

const Message = styled('p', {
  fontSize: '0.95rem',
  lineHeight: 1.55,
  color: '#555',
  margin: '0 0 1.5rem',
});



/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface IncompleteProfileOverlayProps {
  missingFields: string[];
}

export const IncompleteProfileOverlay: React.FC<IncompleteProfileOverlayProps> = ({
  missingFields,
}) => {
  return (
    <Backdrop>
      <Card>
        <IconCircle variant="incomplete">
          <ShieldExclamation size={28} />
        </IconCircle>
        <Title>Profile Incomplete</Title>
        <Message>
          Complete your profile to start browsing tutors. You still need to add: {missingFields.join(', ').toLowerCase()}.
        </Message>
        <Link to="/profile" style={{ textDecoration: 'none' }}>
          <Button tone="success" block>Complete Your Profile</Button>
        </Link>
      </Card>
    </Backdrop>
  );
};

interface UnverifiedAccountOverlayProps {
  isProfileComplete: boolean;
}

export const UnverifiedAccountOverlay: React.FC<UnverifiedAccountOverlayProps> = ({
  isProfileComplete,
}) => {
  return (
    <Backdrop>
      <Card>
        <IconCircle variant={isProfileComplete ? 'pending' : 'incomplete'}>
          <ShieldExclamation size={28} />
        </IconCircle>

        {isProfileComplete ? (
          <>
            <Title>Verification Pending</Title>
            <Message>
              Your profile is complete and under review. You'll be able to
              browse parents once your account has been verified. Sit tight!
            </Message>
          </>
        ) : (
          <>
            <Title>Account Not Verified</Title>
            <Message>
              Your profile isn't complete yet, which means it can't be verified.
              Head over to your profile to fill in the missing details.
            </Message>
            <Link to="/profile" style={{ textDecoration: 'none' }}>
              <Button tone="success" block>Complete Your Profile</Button>
            </Link>
          </>
        )}
      </Card>
    </Backdrop>
  );
};